import React, { useRef } from 'react';

import { TOUR_STEP } from '@candu-ai/styleguide/selectors';
import { toCssSelector } from '@candu-ai/stylesheet/utils';
import { useTooltipPosition } from 'craft/resolvers/Hotspot/utils/useTooltipPosition';
import { getSelectorFromIframe } from 'craft/utils/snapshot-iframe';

interface ElementBorderProps {
  top: number;
  left: number;
  height: number;
  width: number;
  variant: string;
}

const ElementBorder = ({ top, left, height, width, variant }: ElementBorderProps) => {
  const containerClassname = toCssSelector(TOUR_STEP.BackdropContainer, {
    variant: { variant },
    output: 'html',
  });

  return <div className={containerClassname} style={{ top, left, width, height }} />;
};

interface BackdropContainerProps {
  position: 'top' | 'left' | 'right' | 'bottom';
  top: number;
  left: number;
  bottom: number;
  height: number;
  width: number;
  variant: string;
}

const BackdropContainer = ({
  position,
  top,
  left,
  bottom,
  height,
  width,
  variant,
}: BackdropContainerProps) => {
  const wrapperClassname = toCssSelector(TOUR_STEP.BackdropWrapper, {
    variant: { variant },
    output: 'html',
  });

  switch (position) {
    case 'top':
      return (
        <div
          className={wrapperClassname}
          style={{
            top: 0,
            left: 0,
            right: 0,
            height: top,
          }}
        />
      );
    case 'left':
      return (
        <div
          className={wrapperClassname}
          style={{
            top,
            height,
            width: left,
            left: 0,
          }}
        />
      );
    case 'right':
      return (
        <div
          className={wrapperClassname}
          style={{
            top,
            left: left + width,
            right: 0,
            height,
          }}
        />
      );
    default:
    case 'bottom':
      return (
        <div
          className={wrapperClassname}
          style={{
            top: bottom,
            left: 0,
            right: 0,
            bottom: 0,
          }}
        />
      );
  }
};

interface Props {
  variant: string;
  tourStepSelector: string;
}

export const Backdrop = ({ variant, tourStepSelector }: Props) => {
  const placementSelectorEl = getSelectorFromIframe(tourStepSelector);
  const beaconRef = useRef<Element | undefined>(placementSelectorEl ?? undefined);

  useTooltipPosition({
    // @ts-expect-error
    beaconRef,
    selector: tourStepSelector,
  });

  const clientProps = beaconRef?.current?.getBoundingClientRect();
  if (!clientProps) {
    return null;
  }

  return (
    <>
      <ElementBorder
        variant={variant}
        top={clientProps.top}
        left={clientProps.left}
        width={clientProps.width}
        height={clientProps.height}
      />
      <BackdropContainer
        variant={variant}
        position="top"
        top={clientProps.top}
        bottom={clientProps.bottom}
        left={clientProps.left}
        width={clientProps.width}
        height={clientProps.height}
      />
      <BackdropContainer
        variant={variant}
        position="left"
        top={clientProps.top}
        bottom={clientProps.bottom}
        left={clientProps.left}
        width={clientProps.width}
        height={clientProps.height}
      />
      <BackdropContainer
        variant={variant}
        position="right"
        top={clientProps.top}
        bottom={clientProps.bottom}
        left={clientProps.left}
        width={clientProps.width}
        height={clientProps.height}
      />
      <BackdropContainer
        variant={variant}
        position="bottom"
        top={clientProps.top}
        bottom={clientProps.bottom}
        left={clientProps.left}
        width={clientProps.width}
        height={clientProps.height}
      />
    </>
  );
};
